import React, { useState, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Autocomplete from '@mui/material/Autocomplete';
import startCase from 'lodash/startCase';
import type { TextInputDialogOptions, TextInputDialogField, SelectOption } from '../types';
import KeyShortcut from './KeyShortcut';
import {
  autocompleteTextFieldSlotProps,
  omitAutocompleteTextFieldParams,
} from '@erikdakoda/ui/shared/autocompleteTextFieldSlotProps';
import mergeSxProps from '@erikdakoda/ui/shared/mergeSxProps';
import { useDialogFullScreen } from '@erikdakoda/ui/hooks/useDialogFullScreen';

type NormalizedInput = {
  name: string;
  label: string;
  value: string | number | boolean;
  type: 'string' | 'number' | 'boolean';
  options?: SelectOption[];
};

/**
 * Normalizes input field to object format with name, label, value, type, and options.
 */
function normalizeInput(input: TextInputDialogField): NormalizedInput {
  if (typeof input === 'string') {
    return { name: input, label: startCase(input), value: '', type: 'string' };
  }

  const type = input.type ?? 'string';
  let value: string | number | boolean;

  if (input.value !== undefined) {
    value = input.value;
  } else {
    // Default values based on type
    if (type === 'boolean') {
      value = false;
    } else if (type === 'number') {
      value = 0;
    } else {
      value = '';
    }
  }

  return {
    name: input.name,
    label: input.label ?? startCase(input.name),
    value,
    type,
    options: input.options,
  };
}

type TextInputDialogProps<T extends Record<string, string | number | boolean>> = {
  open: boolean;
  options: TextInputDialogOptions;
  onConfirm: (values: T) => void;
  onCancel: () => void;
};

export default function TextInputDialog<T extends Record<string, string | number | boolean>>({
  open,
  options,
  onConfirm,
  onCancel,
}: TextInputDialogProps<T>) {
  'use memo';

  const fullScreen = useDialogFullScreen();
  const { inputs, dialogBoxProps = {} } = options;
  const {
    title,
    titleProps,
    description,
    contentProps,
    confirmationText = 'Ok',
    cancellationText = 'Cancel',
    dialogProps,
    dialogActionsProps,
    confirmationButtonProps,
    cancellationButtonProps,
  } = dialogBoxProps;

  const firstInputRef = useRef<HTMLInputElement>(null);

  // Normalize inputs to consistent object format
  // keep: identity is a dependency of the reset effect below (recomputing every render would refire it every render)
  const normalizedInputs = useMemo(() => inputs.map(normalizeInput), [inputs]);

  const [values, setValues] = useState<Record<string, string | number | boolean>>(() =>
    normalizedInputs.reduce(
      (acc, input) => {
        acc[input.name] = input.value;
        return acc;
      },
      {} as Record<string, string | number | boolean>,
    ),
  );

  // Reset values when inputs change (new dialog opened)
  useEffect(() => {
    setValues(
      normalizedInputs.reduce(
        (acc, input) => {
          acc[input.name] = input.value;
          return acc;
        },
        {} as Record<string, string | number | boolean>,
      ),
    );
  }, [normalizedInputs]);

  const handleChange = (name: string, value: string | number | boolean) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirm = () => {
    onConfirm(values as T);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleConfirm();
    }
  };

  // Move focus into the first field once the dialog has finished opening, rather than via the
  // `autoFocus` prop (jsx-a11y/no-autofocus)
  const handleTransitionEntered = () => {
    const element = firstInputRef.current;
    if (!element) {
      return;
    }
    element.focus();
    if (element.type === 'text' || element.type === 'number') {
      element.select();
    }
  };

  return (
    <Dialog
      fullWidth
      fullScreen={fullScreen}
      {...dialogProps}
      className={clsx(dialogProps?.className, 'text-input-dialog')}
      open={open}
      onClose={onCancel}
      onKeyDown={handleKeyDown}
      slotProps={{ transition: { onEntered: handleTransitionEntered } }}
    >
      {title && <DialogTitle {...titleProps}>{title}</DialogTitle>}
      <DialogContent {...contentProps} sx={mergeSxProps({ '&&': { pt: 3 } }, contentProps?.sx)}>
        {description && <DialogContentText component="div">{description}</DialogContentText>}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: description ? 2 : 0,
          }}
        >
          {normalizedInputs.map((input, index) => {
            if (input.type === 'boolean') {
              return (
                <FormControlLabel
                  key={input.name}
                  control={
                    <Switch
                      checked={Boolean(values[input.name])}
                      onChange={(e) => handleChange(input.name, e.target.checked)}
                      slotProps={index === 0 ? { input: { ref: firstInputRef } } : undefined}
                    />
                  }
                  label={input.label}
                  sx={{ marginLeft: 2 }}
                />
              );
            }

            // Render Autocomplete if options are provided
            if (input.options && input.options.length > 0) {
              const currentValue = values[input.name];
              const selectedOption = input.options.find((opt) => opt.id === currentValue) ?? null;

              return (
                <Autocomplete
                  key={input.name}
                  options={input.options}
                  getOptionLabel={(option) => option.label ?? String(option.id)}
                  getOptionDisabled={(option) => option.disabled ?? false}
                  value={selectedOption}
                  onChange={(_event, newValue) => {
                    handleChange(input.name, newValue?.id ?? '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...omitAutocompleteTextFieldParams(params)}
                      name={input.name}
                      label={input.label}
                      fullWidth
                      inputRef={index === 0 ? firstInputRef : undefined}
                      slotProps={autocompleteTextFieldSlotProps(params)}
                    />
                  )}
                  fullWidth
                />
              );
            }

            const isNumber = input.type === 'number';
            return (
              <TextField
                key={input.name}
                name={input.name}
                label={input.label}
                type={isNumber ? 'number' : 'text'}
                value={values[input.name] ?? (isNumber ? 0 : '')}
                onChange={(e) =>
                  handleChange(input.name, isNumber ? Number(e.target.value) : e.target.value)
                }
                fullWidth
                inputRef={index === 0 ? firstInputRef : undefined}
              />
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions {...dialogActionsProps}>
        <Button {...cancellationButtonProps} onClick={onCancel}>
          {cancellationText} <KeyShortcut eventKey="Escape" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          {...confirmationButtonProps}
          onClick={handleConfirm}
        >
          {confirmationText} <KeyShortcut eventKey="Enter" />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
